/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *           _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * Angular Service that fetches the users media data, i.e microphone, camera, etc...
 */
import { Injectable } from '@angular/core';

@Injectable()
export class BulbFetchMediaDataService {
  /**
   * Static reference to the navigator interface that represents the state and the identity of the user agent.
   */
  private static readonly navigator = window.navigator;

  /**
   * Default constructor.
   */
  constructor() {}

  /**
   * Function that interactively requests access to the users media device(s).
   *
   * @param constraints An object specifying the types of media to request, along with any requirements for
   * each type.
   */
  fetchMedia(constraints : any): Promise< MediaStream > {
    return BulbFetchMediaDataService.navigator.mediaDevices.getUserMedia(constraints);
  }

  /**
   * Checks to see if the user has an external audio input device i.e Microphone.
   *
   * @param isSafariBrowser
   */
  isUsingExternalAudioInputDevice(isSafariBrowser: boolean): Promise< any > {
    return new Promise((resolve, reject) => {
      const { mediaDevices } = BulbFetchMediaDataService.navigator;
      if (!BulbFetchMediaDataService.navigator.mediaDevices || !BulbFetchMediaDataService.navigator.mediaDevices.enumerateDevices) {
        reject('enumerateDevices() not supported.');
      } else {
        // *VERY IMPORTANT* Safari Desktop handles enumerating over the media devices significantly different then Chrome.
        // Might want to extend it to using with Chrome one day, hence the conditional here.
        if (isSafariBrowser) {
          // *IMPORTANT PART*
          mediaDevices.getUserMedia({ audio: true }).then((stream) => {
            // Kill media stream since we only need to fetch audio input devices:
            stream.getAudioTracks().forEach((track) => {
              track.stop();
            });
            mediaDevices.enumerateDevices().then((devices) => {
              const filterAudioInputDevices = devices.filter((device) => device.kind === 'audioinput').filter(
                (audioInputDevice) => (audioInputDevice.label !== 'Built-in Microphone'),
              );
              resolve(filterAudioInputDevices.length >= 1);
            });
          });
        }
      }
    });
  }
}
